// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { OrgDefaultCurrencyCode } from '@/constants/currency';
import { Currency } from '@/currency/api/types';
import { serializeOptionalCurrencyForAPI } from '@/currency/utilities/serialize';
import { itemDetailSchema } from '@/items/api/types';
import { z } from 'zod';

// Each cost field is sent as a currency value. The accounting currency is also sent
// explicitly as costCurrencyCode so a currency-only change persists even when every
// cost amount is null. It is typed to the app-allowed currencies rather than the
// full recognized registry, which is what the backend accepts.
export interface UpdateItemAccountingParameters {
  usefulLife?: number | null;
  costCurrencyCode?: OrgDefaultCurrencyCode;
  salvageValue?: Currency | null;
  simpleCostBasis?: Currency | null;
  billOfMaterials?: Currency | null;
  directLabor?: Currency | null;
  allocatedIndirect?: Currency | null;
  freightInbound?: Currency | null;
  freightOutbound?: Currency | null;
  installation?: Currency | null;
  tax?: Currency | null;
  tariffs?: Currency | null;
  interest?: Currency | null;
}

const updateItemAccountingResponseSchema = z
  .object({ data: itemDetailSchema, metadata: responseMetadataSchema })
  .transform(({ data, ...response }) => ({ ...response, item: data }));

type UpdateItemAccountingResponse = z.infer<
  typeof updateItemAccountingResponseSchema
>;

/**
 * Updates accounting defaults for an item.
 */
export async function updateItemAccounting(
  itemKey: string,
  parameters: UpdateItemAccountingParameters
): Promise<UpdateItemAccountingResponse> {
  const url = `/v1/catalog/item/${itemKey}/accounting`;
  const response = await api.patch(url, {
    ...parameters,
    salvageValue: serializeOptionalCurrencyForAPI(parameters.salvageValue),
    simpleCostBasis: serializeOptionalCurrencyForAPI(
      parameters.simpleCostBasis
    ),
    billOfMaterials: serializeOptionalCurrencyForAPI(
      parameters.billOfMaterials
    ),
    directLabor: serializeOptionalCurrencyForAPI(parameters.directLabor),
    allocatedIndirect: serializeOptionalCurrencyForAPI(
      parameters.allocatedIndirect
    ),
    freightInbound: serializeOptionalCurrencyForAPI(parameters.freightInbound),
    freightOutbound: serializeOptionalCurrencyForAPI(
      parameters.freightOutbound
    ),
    installation: serializeOptionalCurrencyForAPI(parameters.installation),
    tax: serializeOptionalCurrencyForAPI(parameters.tax),
    tariffs: serializeOptionalCurrencyForAPI(parameters.tariffs),
    interest: serializeOptionalCurrencyForAPI(parameters.interest),
  });
  return parseResponse(updateItemAccountingResponseSchema, response.data, {
    schemaName: 'UpdateItemAccounting',
    endpoint: url,
  });
}
